import BlogArticle from '@/components/feature/BlogArticle';

const props = {
  "slug": "volet-roulant-bloque-que-faire",
  "category": "Volet roulant",
  "seoTitle": "Volet roulant bloqué : que vérifier avant d'appeler un pro",
  "metaDescription": "Volet roulant bloqué ou coincé à Lyon ? Localiser le blocage, les vérifications à faire sans rien casser et quand appeler un professionnel.",
  "h1": "Volet roulant bloqué : que vérifier avant d'appeler un réparateur",
  "datePublished": "2026-07-23",
  "dateLabel": "23 juillet 2026",
  "readingTime": "7 min",
  "heroImage": "/images/dfpro-hero-2026.jpg",
  "heroAlt": "Volet roulant bloqué à Lyon, diagnostic et déblocage",
  "ctaTitle": "Volet bloqué, panne de moteur ?",
  "ctaText": "DFPRO Lyon intervient sous 24-48h, 7j/7. Devis gratuit, garantie 10 ans.",
  "intro": [
    "Un volet roulant qui se bloque, à mi-course ou complètement, est à la fois agaçant et parfois source d'insécurité — surtout s'il reste ouvert la nuit ou fermé en pleine journée. Avant de conclure à une panne grave, sachez qu'un blocage a souvent une cause simple et identifiable.",
    "L'erreur classique est de forcer pour « débloquer ». C'est justement ce qui transforme un incident mineur en réparation lourde. Dans ce guide, nous voyons comment localiser précisément le blocage, ce que vous pouvez vérifier vous-même en sécurité, et à quel moment l'intervention d'un professionnel devient indispensable."
  ],
  "sections": [
    {
      "h2": "Localiser le blocage : à quel endroit ça coince ?",
      "body": [
        "Observez attentivement où le volet s'arrête. Un blocage <strong>en tout début de remontée</strong>, moteur qui force, évoque un tablier lourd ou grippé. Un blocage <strong>à mi-hauteur</strong> pointe le plus souvent vers une lame désaxée ou un obstacle dans les coulisses latérales. Un blocage <strong>en fin de course</strong> (haut ou bas) suggère un problème de réglage des butées sur un volet électrique.",
        "Notez aussi le comportement : le volet ne bouge pas du tout, bouge par à-coups, ou fait du bruit sans avancer ? Chaque symptôme oriente vers une famille de causes différente."
      ]
    },
    {
      "h2": "Les causes mécaniques les plus fréquentes",
      "body": [
        "La <strong>lame finale ou une lame intermédiaire sortie de sa coulisse</strong> est un grand classique : le tablier se coince en biais. Un <strong>obstacle dans le rail</strong> (petit caillou, débris, nid d'insecte) suffit à tout bloquer. Une <strong>coulisse déformée</strong> ou encrassée freine la descente et la remontée.",
        "Sur les volets manuels, une <a href=\"/changer-sangle-volet-roulant-lyon/\" class=\"text-primary-600 font-medium underline\">sangle abîmée</a> ou un mécanisme de manivelle usé empêche l'entraînement. Ces éléments se remplacent sans changer l'ensemble du volet."
      ]
    },
    {
      "h2": "Les causes électriques (volet motorisé)",
      "body": [
        "Si votre volet est motorisé et ne réagit plus, testez d'abord la commande : pile de télécommande, autre point de commande, disjoncteur. Un moteur qui <strong>bourdonne sans bouger</strong> trahit un condensateur fatigué ; un volet qui <strong>s'arrête toujours au même endroit</strong> révèle des fins de course déréglées ou une sécurité thermique déclenchée (le moteur a chauffé et se protège).",
        "Ces pannes se traitent par un <a href=\"/reparation-moteur-volet-roulant-lyon/\" class=\"text-primary-600 font-medium underline\">dépannage du moteur</a> ciblé — condensateur, réglage, télécommande — sans forcément remplacer le bloc complet."
      ]
    },
    {
      "h2": "Les vérifications que vous pouvez faire en sécurité",
      "body": [
        "Coupez l'alimentation avant toute manipulation sur un volet électrique. Inspectez visuellement les deux coulisses latérales sur toute la hauteur accessible : une lame légèrement sortie ou un débris sont parfois visibles à l'œil. Nettoyez l'entrée des rails.",
        "Sur un volet manuel, vérifiez l'état apparent de la sangle et le fonctionnement de la manivelle. Si tout semble intact mais que le tablier refuse toujours de bouger, n'insistez pas : le problème est interne."
      ]
    },
    {
      "h2": "Quand appeler un professionnel — et pourquoi ne pas forcer",
      "body": [
        "Dès que le tablier est désaxé, que des lames sont sorties, que le caisson doit être ouvert ou que le moteur ne répond plus, l'intervention d'un professionnel s'impose. Forcer un volet coincé est la première cause d'aggravation que nous constatons : quelques secondes de forçage peuvent plier tout un tablier.",
        "DFPRO Lyon réalise le <a href=\"/volet-roulant-bloque-lyon/\" class=\"text-primary-600 font-medium underline\">déblocage de volet roulant</a> proprement, remet le tablier en état et vérifie l'ensemble du mécanisme. Intervention sous 24-48h à Lyon, devis gratuit, garantie 10 ans."
      ]
    }
  ],
  "faq": [
    {
      "q": "Mon volet est bloqué en bas, comment faire entrer la lumière ?",
      "a": "Ne tentez pas de soulever le tablier de force : vous risquez de plier les lames et d'aggraver le blocage. Coupez l'alimentation si le volet est électrique et appelez un professionnel. À Lyon, DFPRO intervient sous 24-48h, 7j/7, au 09 80 80 36 84."
    },
    {
      "q": "Un volet qui coince, est-ce réparable ou faut-il tout changer ?",
      "a": "Dans la très grande majorité des cas, c'est réparable : lame, coulisse, sangle, moteur ou condensateur se remplacent séparément. Le remplacement complet ne se justifie que si le tablier est très endommagé ou si le volet est vraiment vétuste."
    },
    {
      "q": "Pourquoi mon volet fait du bruit mais ne bouge pas ?",
      "a": "Sur un volet électrique, un bourdonnement sans mouvement indique presque toujours un condensateur en fin de vie. Sur un volet manuel, un bruit sans effet évoque une sangle déboîtée ou un treuil qui patine. Dans les deux cas, la pièce concernée se remplace."
    },
    {
      "q": "Le déblocage est-il garanti ?",
      "a": "Oui, toutes nos réparations bénéficient d'une garantie 10 ans, et le devis est gratuit et présenté avant l'intervention."
    }
  ],
  "related": [
    {
      "title": "Volet roulant qui ne remonte plus : causes et solutions",
      "href": "/blog/volet-roulant-ne-remonte-plus/"
    },
    {
      "title": "Moteur de volet roulant en panne : diagnostic",
      "href": "/blog/moteur-volet-roulant-en-panne/"
    },
    {
      "title": "Changer la sangle d'un volet roulant",
      "href": "/blog/changer-sangle-volet-roulant/"
    }
  ]
};

export default function VoletRoulantBloqueQueFaire() {
  return <BlogArticle {...props} />;
}
